import {
    ArcElement,
    BarElement,
    BarController,
    CategoryScale,
    Chart as ChartJS,
    Colors,
    Filler,
    Legend,
    LineElement,
    LineController,
    LinearScale,
    PointElement,
    PieController,
    ScatterController,
    Title,
    Tooltip
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import Color from "color";
import { distinct } from "../../utils/arrayUtilities";
import { buildData } from "./chartUtils";
import PropTypes from 'prop-types';
import { withComponentCapabilities } from '../capabilities/componentCapabilities';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
    Filler,
    Colors,
    PieController,
    LineController,
    ScatterController
);
const DEFAULT_PX = 800; 

export function buildChartJsConfig(chart, data) {
    const chartData = {
        labels: chart.labels, 
        datasets: chart.series.map(s => {
            const config = { type: s.type, horizontal: s.horizontal, label: s.label, data: s.data ??  buildData(data, s.query, s.xField, s.collation, null, s.mapping) };
            switch (s.type ?? chart.type) {
                case 'radar':
                case 'area':
                    // area graph is not supported by ChartJS so it is converted to a line chart with fill
                    config.type = 'line';
                    config.fill = 'start';
                    if (s.color) {
                        config.backgroundColor = Color(s.color).alpha(0.5).rgb().toString();
                        config.borderColor = s.color;
                    }
                    break;
                case 'line':
                    if (s.color) {
                        config.borderColor = s.color;
                    }
                    break;
                case 'bubble':
                case 'bar':
                    if (s.color) {
                        config.backgroundColor = Array.isArray(s.color)
                            ? s.color.map(c => Color(c).alpha(0.5).rgb().toString())
                            : Color(s.color).alpha(0.5).rgb().toString();
                        config.borderColor = s.color;
                    }
                    break;
                default:
                    break;
            }
            return config;
        })
    };
    if (chartData.datasets.some(entry => entry.type === 'pie') || chartData.datasets.some(entry => entry.type === 'doughnut') || chartData.datasets.some(entry => entry.type === 'polarArea') || chartData.datasets.some(entry => entry.type === 'radar')) {
        let labels = chartData.datasets.flatMap(entry => entry.data.map(({ x }) => ({ x })));
        labels = distinct(labels, 'x').map(e => e.x);
        const newSets = chartData.datasets.map(entry => {
            const newData = labels.map(l => entry.data.find(d => d.x === l)?.y ?? 0);
            const set = { ...entry, data: newData };
            return set;
        });
        chartData.datasets = newSets.filter(entry => entry.data?.length > 0);
        chartData.labels = labels;
    }
    chartData.datasets.forEach(entry => {
        if (entry.horizontal) {
            entry.indexAxis = 'y';
            entry.data = entry.data.map(({ x, y }) => ({ x: y, y: x }))
            delete entry.horizontal;
        }
    })

    if (chartData.mapping) {
        const labels = Object.values(chartData.mapping);
        chartData.labels = labels;
        chartData.data = labels.map(label => {
            const found = chartData.data.find(d => d.x === label);
            return found ? found : { x: label, y: 0 };
        });
    }   

    const options = {
        maintainAspectRatio: false,
        plugins: {
            legend: { display: chart.series.length > 1 },
            ...(chart.options?.plugins || {}),
        },
        ...chart.options,
    };

    return { data: chartData, options };
}

export async function generateStaticChartImage(chart, data, height, width ) {
    const firstType = chart?.series?.[0]?.type;
    const baseType = chart?.type ?? firstType ?? "bar";

    const h = (height ?? 1);
    const w = (width ?? 1);

    const scale = Math.min(DEFAULT_PX / h, DEFAULT_PX / w);
    const H = Math.round(h * scale);
    const W = Math.round(w * scale);

    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;

    const context = canvas.getContext("2d");
    if (!context) {
        throw new Error("2D context unavailable");
    }

    const { data: chartData, options } = buildChartJsConfig(chart, data);
    const wantValueLabels = options?.showValues === true;
    const valueLabelPlugin = wantValueLabels ? {
        id: 'valueLabelPlugin',
        afterDatasetsDraw(c) {
            const { ctx } = c;
            ctx.save();
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            c.data.datasets.forEach((ds, di) => {
                const meta = c.getDatasetMeta(di);
                if (!meta || meta.hidden) return;
                meta.data.forEach((elem, i) => {
                    const raw = ds.data?.[i];
                    const val = raw?.y;
                    if (val == null) return;
                    const pos = elem.tooltipPosition ? elem.tooltipPosition() : (elem.getCenterPoint ? elem.getCenterPoint() : { x: elem.x, y: elem.y });
                    ctx.fillText(val.toString(), pos.x, pos.y);
                });
            });
            ctx.restore();
        }
    } : null;

    const config = {
        type: baseType,
        data: chartData,
        options: {
            ...options,
            plugins: {
                title: {
                    display: true,
                    text: chart.title,
                },
                ...(options?.plugins || {}),
            },
            responsive: false,
            animation: false,
            
        },
        plugins: [[valueLabelPlugin]]
    };
    
    const chartInstance = new ChartJS(context, config);
    chartInstance.update();

    const dataUrl = canvas.toDataURL("image/png");
    const base64 = dataUrl.split(",")[1];
    chartInstance.destroy();

    return {
        data: base64,
        extension: ".png",
        mime: "image/png",
        width,
        height
    }}

/**
 * A dynamic data visualization component
 * @component 
 */
const DynamicChart = withComponentCapabilities(({ chart, data }) => {
    const { data: chartData, options } =  buildChartJsConfig(chart, data);
    return <Chart data={chartData} options={{ responsive: true, ...options }}></Chart>
}, {
    lazyProps: ['data']
});

DynamicChart.propTypes = {
    /**
     * The chart configuration
     */
    chart: PropTypes.object,
    /**
     * The data to be rendered by the chart
     */
    data: PropTypes.array
}

export default DynamicChart;